/*
  File: src/pages/WatchPage.jsx
  Purpose: Streaming watch page that renders the IVS player with controls and sidebar.
*/
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import IVSPlayer from "../components/Stream/IVSPlayer";
import Navbar from "../components/layout/Navbar";
import SidebarFeed from "../components/Stream/SidebarFeed";
import DetectionsBar from "../components/Stream/DetectionsBar";
import PerformanceSidebar from "../components/Stream/PerformanceSidebar";
import "./watchBrand.css";
import StreamingControls from "../components/Stream/StreamingControls";
import { StreamingProvider, useStreaming, STREAMING_STATES } from "../contexts/StreamingContext";
import { getCachedPlayers } from "../utils/playerCache";
import DetectionsConfig from "../config/detections";
import StopStreamModal from "../components/Stream/StopStreamModal";

function WatchContent() {
    const navigate = useNavigate();
    const suppressUntilRef = useRef(Date.now() + 5000);
    const [history, setHistory] = useState([]);
    const [overlayItem, setOverlayItem] = useState(null);
    const offlineStartTimeRef = useRef(null);
    const offlineTimeoutRef = useRef(null);
    const statusCheckIntervalRef = useRef(null);
    const [showStopModal, setShowStopModal] = useState(false);

    const {
        streamingState,
        isLoading,
        playbackUrl,
        error,
        checkExistingStream,
        waitAndConnect,
        streamSessionId,
        streamingService,
        streamType,
        stopStream,
        clearStreamState
    } = useStreaming();

    const API_BASE = (process.env.REACT_APP_API_BASE || '').trim().replace(/^@+/, '') || '/dev';

    // Track if sidebar is visible (on large screens, lg breakpoint is 1024px)
    const [sidebarVisible, setSidebarVisible] = useState(window.innerWidth >= 1024);
    useEffect(() => {
        const handleResize = () => setSidebarVisible(window.innerWidth >= 1024);
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    // Function to reset all match status to 'Not Started'
    const resetAllMatchStatus = useCallback(async () => {
        try {
            // Fetch all matches
            const res = await fetch(`${API_BASE}/matches`, { headers: { accept: 'application/json' } });
            if (res.ok) {
                const data = await res.json();
                const items = Array.isArray(data?.items) ? data.items : [];
                
                // Find any live matches and stop them
                const liveMatchIds = items.filter(m => String(m.status || '').toLowerCase() === 'live').map(m => m.id);
                
                for (const matchId of liveMatchIds) {
                    try {
                        await fetch(`${API_BASE}/matches/live/stop`, {
                            method: 'POST',
                            headers: { 'Content-Type': 'application/json' },
                            body: JSON.stringify({ id: matchId })
                        });
                    } catch (e) {
                        console.warn(`Failed to stop match ${matchId}:`, e);
                    }
                }
                
                console.log('All match status reset to Not Started');
            }
        } catch (e) {
            console.warn('Failed to reset match status:', e);
        }
    }, [API_BASE]);

    // Function to check stream status
    const checkStreamStatus = useCallback(async () => {
        if (!streamingService) return; // Guard against undefined service
        
        try {
            const status = await streamingService.getStatus('match');
            const isLive = status?.ivs_state === 'LIVE' || String(status?.status || '').toLowerCase() === 'live';
            
            if (!isLive) {
                // Stream is offline
                if (!offlineStartTimeRef.current) {
                    offlineStartTimeRef.current = Date.now();
                    console.log('Stream went offline, starting 3-second countdown');
                } else {
                    // Check if 3 seconds have passed
                    const offlineDuration = Date.now() - offlineStartTimeRef.current;
                    if (offlineDuration >= 3000) {
                        console.log('Stream offline for 3+ seconds, resetting matches and redirecting');
                        await resetAllMatchStatus();
                        navigate('/football');
                        return;
                    }
                }
            } else {
                // Stream is live, reset offline tracking
                if (offlineStartTimeRef.current) {
                    console.log('Stream came back online');
                    offlineStartTimeRef.current = null;
                }
            }
        } catch (e) {
            console.warn('Failed to check stream status:', e);
            // On error, treat as offline
            if (!offlineStartTimeRef.current) {
                offlineStartTimeRef.current = Date.now();
            }
        }
    }, [streamingService, resetAllMatchStatus, navigate]);

    // Start status monitoring when component mounts
    useEffect(() => {
        const intervalId = setInterval(checkStreamStatus, 2000); // Check every 2 seconds
        statusCheckIntervalRef.current = intervalId;
        
        return () => {
            if (intervalId) {
                clearInterval(intervalId);
            }
            if (offlineTimeoutRef.current) {
                clearTimeout(offlineTimeoutRef.current);
            }
        };
    }, [checkStreamStatus]);

    const handleMetadata = useCallback(async (data) => {
        if (Date.now() < (suppressUntilRef.current || 0)) return;
        const base = (process.env.REACT_APP_API_BASE || '/dev');
        const meta = data || {};

        // Extract search combos (team/player/number) similar to DetectionsBar
        const extractCombos = (m) => {
            const out = [];
            if (!m || typeof m !== 'object') return out;
            const arr = Array.isArray(m.teams) ? m.teams : [];
            arr.forEach((t) => {
                const team = t?.t || t?.team || '';
                const inst = Array.isArray(t?.i) ? t.i : (Array.isArray(t?.instances) ? t.instances : []);
                if (inst.length > 0) {
                    inst.forEach((x) => {
                        let num;
                        const n = x?.n;
                        if (n && typeof n === 'object' && !Array.isArray(n)) num = n.t ?? n.value;
                        else if (Array.isArray(n) && n.length > 0) {
                            const first = n[0];
                            num = typeof first === 'object' && first !== null ? (first.v ?? first.value ?? first.t) : first;
                        }
                        out.push({ team, player: x?.p || x?.player, number: num });
                    });
                } else {
                    let num;
                    const n = t?.n;
                    if (Array.isArray(n) && n.length > 0) {
                        const first = n[0];
                        num = typeof first === 'object' && first !== null ? (first.v ?? first.value ?? first.t) : first;
                    } else if (typeof n === 'number' || typeof n === 'string') num = n;
                    out.push({ team, player: t?.p || t?.player, number: num });
                }
            });
            return out;
        };

        // Ensure we have client receive timestamp from IVSPlayer; fallback if missing
        const recvMs = (meta && meta.tim && typeof meta.tim.clt_recv_ms === 'number') ? meta.tim.clt_recv_ms : Date.now();

        // Measure search timing first, then display and persist
        (async () => {
            // Prepare batch queries for timing
            const combos = extractCombos(meta);
            const queries = combos.map((f) => {
                const teamRaw = f.team ? String(f.team).toLowerCase() : '';
                // face_only: only send the player name and omit team entirely
                if (teamRaw === 'face_only') {
                    const names = f.player ? [String(f.player)] : [];
                    return { names };
                }
                const tokens = [];
                if (typeof f.number !== 'undefined' && f.number !== null && String(f.number) !== '') tokens.push(String(f.number));
                if (f.player) tokens.push(String(f.player));
                return { team: teamRaw || null, names: tokens };
            });

            let searchStart;
            let searchEnd;
            if (queries.length > 0) {
                if (DetectionsConfig.playerCacheEnabled) {
                    const cached = getCachedPlayers(queries);
                    const need = cached.filter((c) => !c || !(c.id || c.display_name || c.image_url)).length;
                    if (need === 0) {
                        const t = Date.now();
                        searchStart = t;
                        searchEnd = t;
                    } else {
                        try {
                            searchStart = Date.now();
                            await fetch(`${base}/players/search`, {
                                method: 'POST',
                                headers: { 'content-type': 'application/json', accept: 'application/json' },
                                body: JSON.stringify({ queries })
                            });
                            searchEnd = Date.now();
                        } catch (_) {
                            searchEnd = Date.now();
                        }
                    }
                } else {
                    try {
                        searchStart = Date.now();
                        await fetch(`${base}/players/search`, {
                            method: 'POST',
                            headers: { 'content-type': 'application/json', accept: 'application/json' },
                            body: JSON.stringify({ queries })
                        });
                        searchEnd = Date.now();
                    } catch (_) {
                        searchEnd = Date.now();
                    }
                }
            }

            // When search finishes (or is skipped), set shown
            const shownAt = Date.now();
            const finalMeta = {
                ...meta,
                tim: {
                    ...(meta?.tim || {}),
                    clt_recv_ms: recvMs,
                    // New names (preferred)
                    ...(typeof searchStart === 'number' ? { srch_start_ms: searchStart } : {}),
                    ...(typeof searchEnd === 'number' ? { srch_end_ms: searchEnd, srch_ms: Math.max(0, searchEnd - (searchStart || searchEnd)) } : {}),
                    // Backward-compat fields
                    ...(typeof searchStart === 'number' ? { search_start: searchStart } : {}),
                    ...(typeof searchEnd === 'number' ? { search_end: searchEnd, search_ms: Math.max(0, searchEnd - (searchStart || searchEnd)) } : {}),
                    // Display time
                    shown: shownAt,
                    shown_ms: shownAt,
                }
            };

            // First feed the in-player overlay (priority), then insert into history
            setOverlayItem(finalMeta);
            setHistory((prev) => [{ ...finalMeta, id: shownAt }, ...prev]);

            // Post only if this is not a snap-only cue (i.e., we had queries to resolve) and detection history is not disabled
            const isDetectionHistoryDisabled = process.env.REACT_APP_DISABLE_DETECTION_HISTORY === 'true';
            if (streamSessionId && streamingService?.postDetection && queries.length > 0 && !isDetectionHistoryDisabled) {
                streamingService.postDetection(streamSessionId, finalMeta).catch((err) => {
                    console.warn('Failed to record detection:', err?.message || err);
                });
            }
        })();
    }, [streamSessionId, streamingService]);

    const handleLiveToggle = useCallback((_enabled) => {
        // Keep ingesting detections even when overlay is hidden.
        // No state reset here so toggling ON shows the accumulated players.
    }, []);

    const location = useLocation();
    const pendingLive = useMemo(() => Boolean(location?.state && location.state.pendingLive), [location]);
    const matchId = useMemo(() => {
        try {
            const st = location?.state || {};
            const fromState = st.match_id || (st.match && st.match.id) || st.id;
            if (fromState) return Number(fromState);
            const sp = new URLSearchParams(location.search || '');
            const q = sp.get('match_id');
            return q ? Number(q) : null;
        } catch { return null; }
    }, [location]);

    // Persist match id for consumers that read from sessionStorage (defensive)
    useEffect(() => {
        try {
            if (matchId) {
                sessionStorage.setItem('match_id', String(matchId));
                sessionStorage.setItem('current_match_id', String(matchId));
            }
        } catch {}
    }, [matchId]);

    // Resolve a fallback match id from /matches if none was supplied
    const [resolvedMatchId, setResolvedMatchId] = useState(null);
    useEffect(() => {
        let cancelled = false;
        const base = (process.env.REACT_APP_API_BASE || '/dev');
        const applyId = (id) => {
            if (cancelled) return;
            setResolvedMatchId(id);
            try {
                if (id) {
                    sessionStorage.setItem('match_id', String(id));
                    sessionStorage.setItem('current_match_id', String(id));
                }
            } catch {}
        };
        if (matchId) { applyId(matchId); return () => { cancelled = true; }; }
        // Try find an active match from /matches endpoint
        (async () => {
            try {
                const res = await fetch(`${base}/matches?limit=10`, { headers: { accept: 'application/json' } });
                if (!res.ok) return;
                const data = await res.json();
                const items = Array.isArray(data?.items) ? data.items : [];
                if (items.length === 0) return;
                const live = items.find(m => String(m.status || '').toLowerCase() === 'live');
                const chosen = live || items[0];
                if (chosen && chosen.id != null) applyId(Number(chosen.id));
            } catch { /* ignore */ }
        })();
        return () => { cancelled = true; };
    }, [matchId]);
    const customLabelsConfig = useMemo(() => {
        const fromNav = (location?.state && location.state.customLabels) || null;
        if (fromNav) return fromNav;
        try {
            const raw = sessionStorage.getItem('match_custom_labels');
            if (raw) return JSON.parse(raw);
        } catch {}
        return null;
    }, [location]);

    useEffect(() => {
        // Check for existing stream without clearing state to prevent black screen
        checkExistingStream('match');
    }, [checkExistingStream]);

    // Monitor for stream changes when navigating back to /watch
    useEffect(() => {
        const handleStreamChange = async () => {
            if (document.visibilityState !== 'visible') return;
            try {
                const status = await streamingService.getStatus('match');
                const currentSessionId = status?.stream_session_id;
                if (currentSessionId && currentSessionId !== streamSessionId && status?.ivs_state === 'LIVE') {
                    console.log('Detected new stream session, re-connecting to fetch playback URL');
                    // Re-connect using the context helper so state updates consistently
                    await checkExistingStream('match');
                }
            } catch (e) {
                console.warn('Failed to check for stream changes:', e);
            }
        };

        // Check for stream changes when page becomes visible
        document.addEventListener('visibilitychange', handleStreamChange);
        
        // Also check periodically in case stream changes while page is visible
        const intervalId = setInterval(async () => {
            if (document.visibilityState !== 'visible') return;
            try {
                const status = await streamingService.getStatus('match');
                const currentSessionId = status?.stream_session_id;
                if (currentSessionId && currentSessionId !== streamSessionId && status?.ivs_state === 'LIVE') {
                    console.log('Detected stream session change while visible, re-connecting');
                    await checkExistingStream('match');
                }
            } catch (_) {
                // ignore periodic errors
            }
        }, 5000);

        return () => {
            document.removeEventListener('visibilitychange', handleStreamChange);
            clearInterval(intervalId);
        };
    }, [streamingService, streamSessionId, checkExistingStream]);

    // Handle stop modal and navigation
    useEffect(() => {
        if (streamingState === STREAMING_STATES.STOPPING) {
            setShowStopModal(true);
        } else if (streamingState === STREAMING_STATES.IDLE && showStopModal) {
            // Stream stopped successfully, wait briefly then redirect
            setTimeout(() => {
                navigate('/football');
            }, 1500);
        }
    }, [streamingState, showStopModal, navigate]);

    // Custom stop handler that shows modal
    const handleStopStream = useCallback(async () => {
        await stopStream(() => {
            setShowStopModal(true);
        });
    }, [stopStream]);

    // Clean up on unmount
    useEffect(() => {
        return () => {
            if (statusCheckIntervalRef.current) {
                clearInterval(statusCheckIntervalRef.current);
            }
            if (offlineTimeoutRef.current) {
                clearTimeout(offlineTimeoutRef.current);
            }
        };
    }, []);

    // Removed: Auto-connect logic - streams should be started from football page only

    const LIVE_DETECTION_ENABLED = (() => {
        const raw = (process.env.REACT_APP_LIVE_DETECTION ?? process.env.LIVE_DETECTION ?? 'true');
        const v = String(raw).trim().toLowerCase();
        return !(v === '0' || v === 'false' || v === 'no' || v === 'off');
    })();

    return (
        <div className="min-h-screen flex flex-col pt-14 sm:pt-16">
            <Navbar />
            <div className="flex-1">
                {/* Stream session indicator - only show in dev mode */}
                {process.env.REACT_APP_SHOW_DEV_PAGES === 'true' && (
                    <div className="w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-2">
                        <div className="text-[11px] text-white/60">
                            Stream ID: <span className="text-white font-mono">{streamSessionId || '—'}</span>
                        </div>
                    </div>
                )}
                <div className="flex flex-col lg:flex-row gap-4 lg:gap-6 w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-4 sm:pt-6 pb-6 sm:pb-10 relative">
                    {/* Main Video Area */}
                    <div className="flex-1 relative order-1 lg:order-1">
                        <div className="rounded-xl lg:rounded-2xl overflow-hidden card relative">

                            {/* Video Player */}
                            {playbackUrl ? (
                                <IVSPlayer
                                    playbackUrl={playbackUrl}
                                    onMetadata={handleMetadata}
                                    onLiveToggle={handleLiveToggle}
                                    overlayMetadata={overlayItem}
                                    customLabelsConfig={customLabelsConfig}
                                    matchId={resolvedMatchId || matchId}
                                    sidebarVisible={true}
                                />
                            ) : (
                                <div className="w-full aspect-video flex items-center justify-center bg-backgroundDark/80">
                                    <div className="text-center px-4">
                                        <div className="text-gray-300 mb-2 text-sm sm:text-base">
                                            Nothing to watch right now
                                        </div>
                                        <div className="text-xs sm:text-sm text-gray-500">
                                            Start a live stream from the football page to watch here
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Sidebar: move Live Detections to the right (can be disabled via env) */}
                    {LIVE_DETECTION_ENABLED && (
                        <div className="w-full lg:w-80 space-y-4 order-2 lg:order-2">
                            <DetectionsBar history={history} />
                        </div>
                    )}
                </div>
                {/* Bottom: show live stream controls and performance */}
                <div className="w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                        <div className="order-1">
                            <StreamingControls onStop={handleStopStream} />
                        </div>
                        <div className="order-2">
                            <PerformanceSidebar />
                        </div>
                    </div>
                </div>
            </div>
            
            {/* Stop Stream Modal */}
            <StopStreamModal
                isVisible={showStopModal}
                isProcessing={streamingState === STREAMING_STATES.STOPPING}
                streamType="match"
                onComplete={() => setShowStopModal(false)}
            />
        </div>
    );
}

export default function WatchPage() {
    // Resolve API base robustly: strip accidental '@', fallback to relative '/dev'
    const rawBase = (process.env.REACT_APP_API_BASE || "").trim();
    const cleanedBase = rawBase.replace(/^@+/, "");
    const API_BASE = cleanedBase || "/dev";
    const streamingBaseUrl = `${API_BASE}/streaming`;
    // Determine default stream type from URL (?type=series|match), default to 'match'
    const search = typeof window !== 'undefined' ? window.location.search : '';
    const sp = new URLSearchParams(search);
    const defaultType = (sp.get('type') || 'match').toLowerCase() === 'series' ? 'series' : 'match';
    return (
        <StreamingProvider baseUrl={streamingBaseUrl} defaultType={defaultType}>
            <WatchContent />
        </StreamingProvider>
    );
}
